import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const FAQ_ITEMS = [
  {
    question: "Do we need local GPU hardware?",
    answer:
      "No. FeraBio is designed to reduce hardware requirements by running basecalling and analysis in the cloud.",
  },
  {
    question: "Can we run both standard and XNA workflows?",
    answer:
      "Yes. Basecalling supports both with configurable model options, including advanced settings for XNA and modified-base use cases.",
  },
  {
    question: "How can we get access to the closed FeraBio closed beta?",
    answer:
      'Use the following "Request Access" link to request beta access and we will follow up with onboarding steps.',
    inlineLinkLabel: "Request Access",
    inlineLinkRoute: "/waitlist",
  },
];

const ResourcesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const targetId = location.hash.replace("#", "");
    const target = document.getElementById(targetId);
    if (target) target.scrollIntoView({ behavior: "smooth" });
  }, [location.hash]);

  return (
    <section className="subpage-section resources-section">
      <div className="container">
        <h1 className="subpage-title">Resources</h1>
        <p className="subpage-body">
          Guides, onboarding notes, and support contacts to help teams start and
          scale cloud nanopore workflows.
        </p>

        <div id="quickstart" className="resources-block">
          <h2>Quickstart</h2>
          <div className="subpage-feature-grid">
            <div className="subpage-feature-card">
              <h3>1) Upload data and references</h3>
              <p>Prepare datasets and run metadata for cloud processing.</p>
              <Link to="/product/basecalling" className="read-more-link">
                Read more
              </Link>
            </div>
            <div className="subpage-feature-card">
              <h3>2) Select basecalling profile</h3>
              <p>Choose standard or XNA/modified configurations.</p>
              <Link
                to="/product/basecalling#xna-models"
                className="read-more-link"
              >
                Read more
              </Link>
            </div>
            <div className="subpage-feature-card">
              <h3>3) Review and share outputs</h3>
              <p>Inspect QC and share results with collaborators.</p>
              <Link to="/product/tools" className="read-more-link">
                Read more
              </Link>
            </div>
          </div>
        </div>

        <div id="guides" className="resources-block">
          <h2>Guides</h2>
          <div className="subpage-feature-grid">
            <div className="subpage-feature-card">
              <h3>Cloud Basecalling Guide</h3>
              <p>
                Best practices for standard DNA runs and throughput planning.
              </p>
              <Link to="/product/basecalling" className="read-more-link">
                Read more
              </Link>
            </div>
            <div className="subpage-feature-card">
              <h3>XNA and Modified Workflows</h3>
              <p>How to choose model settings for advanced base chemistries.</p>
              <Link
                to="/product/basecalling#xna-models"
                className="read-more-link"
              >
                Read more
              </Link>
            </div>
            <div className="subpage-feature-card">
              <h3>Collaboration and Data Sharing</h3>
              <p>Operational guidance for team review and external sharing.</p>
              <Link to="/product/tools" className="read-more-link">
                Read more
              </Link>
            </div>
          </div>
        </div>

        <div id="faq" className="resources-block">
          <h2>FAQ</h2>
          <div className="faq-accordion">
            {FAQ_ITEMS.map((item) => (
              <details key={item.question} className="faq-item">
                <summary>{item.question}</summary>
                <div className="faq-answer">
                  <p>
                    {item.answer}{" "}
                    {item.inlineLinkRoute ? (
                      <Link
                        to={item.inlineLinkRoute}
                        className="faq-inline-link"
                      >
                        {item.inlineLinkLabel}
                      </Link>
                    ) : null}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>

        <div id="support" className="resources-block resources-support">
          <h2>Contact Support</h2>
          <p>
            Questions about onboarding, pilots, or workflow setup? Reach us at{" "}
            <a href="mailto:jsumabat@uw.edu">jsumabat@uw.edu</a>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ResourcesPage;
